/**
 * 結帳「補登手機號碼」的判斷邏輯——唯一來源。
 *
 * 這是與 otpPolicy.ts（needCheckoutOtp／結帳補驗 OTP）**完全不同**的一件事，刻意分開放：
 *  - needCheckoutOtp：帳號有手機，但那支號碼沒經過 OTP 驗證（掃鋼瓶 barcode 免 OTP 註冊）。
 *  - needPhoneSetup（這裡）：帳號**根本沒有手機**（customer.main_phone 為空）——
 *    LINE 推播邀請註冊進來的客戶就是這種，平台帳號上沒有門號可用。
 *
 * 兩者都成立時只問一次：補登流程本身就走了一次 OTP，成功後即視為已驗證
 *（呼叫端會把 otpVerified 設為 true），不再要客戶連驗兩輪。
 *
 * 純函式、零 import：判斷的輸入全部由呼叫端（useNeedPhoneSetup）餵進來，方便單獨測試。
 */

export interface PhoneSetupState {
  /** 帳號上的手機號碼（customer.main_phone / userData.phone）。空＝沒有。 */
  accountPhone?: string | null;
  /**
   * 帳號資料（GET /customer/home）是否已經回來過。
   *
   * 少了這個旗標就只能看 accountPhone，而「還沒載入」與「帳號真的沒有手機」長得一模一樣：
   * 正常客戶一進結帳頁會先被要求重設手機、送出鍵也被鎖住，等資料回來才自己消失。
   * 未載入一律視為「不需要補登」（fail-open，與 WaitingArrangementGate 同一原則：
   * 資料拿不到不擋下單，後端仍是最後防線）。
   */
  accountLoaded?: boolean;
  /** 客服練習沙盒：假帳號一律當作已有手機，不觸發補登。 */
  practiceMode?: boolean;
}

/** 只留數字後是否為有效的台灣手機門號（與 onboarding 的 NpPhone 同一條規則）。 */
export const isMobilePhone = (phone?: string | null): boolean => /^09\d{8}$/.test((phone || "").replace(/\D/g, ""));

/**
 * 是否需要在結帳頁補登手機號碼（顯示補登區塊 + 擋住送出）。
 *
 * 只要帳號上有任何號碼就算有（市話也算）——這裡守的是「平台帳號沒有聯絡門號」這個缺口，
 * 不是號碼格式。格式檢查留給補登表單自己（那是使用者當下要輸入的值）。
 */
export const shouldSetupPhone = (s: PhoneSetupState): boolean => {
  if (s.practiceMode) return false;
  if (!s.accountLoaded) return false;
  return !(s.accountPhone || "").trim();
};

/** 補登未完成時要擋住「確認送出」。與 shouldSetupPhone 同條件，命名分開讓呼叫端讀得懂。 */
export const shouldBlockSubmitForPhoneSetup = (s: PhoneSetupState): boolean => shouldSetupPhone(s);
